import React, { useRef, useState, useCallback } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { motion } from "framer-motion";
import BorderBeam from "./BorderBeam";

interface MagicCardProps {
  children: React.ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
  hasBorderBeam?: boolean;
  beamColorFrom?: string;
  beamColorTo?: string;
  beamDuration?: number;
  glowColor?: string;
  onClick?: () => void;
  clickable?: boolean;
}

export const MagicCard: React.FC<MagicCardProps> = ({
  children,
  sx,
  hasBorderBeam = false,
  beamColorFrom = "#10B981",
  beamColorTo = "#6366F1",
  beamDuration = 6,
  glowColor = "rgba(16, 185, 129, 0.15)",
  onClick,
  clickable = false,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  return (
    <Box
      ref={cardRef}
      component={motion.div}
      whileHover={clickable || onClick ? { y: -3, transition: { duration: 0.2 } } : undefined}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      sx={{
        position: "relative",
        borderRadius: "20px",
        overflow: "hidden",
        backdropFilter: "blur(16px)",
        cursor: clickable || onClick ? "pointer" : "default",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        ...sx,
      }}
    >
      {/* Optional BorderBeam */}
      {hasBorderBeam && <BorderBeam colorFrom={beamColorFrom} colorTo={beamColorTo} duration={beamDuration} />}

      {/* Mouse Spotlight Glow */}
      <Box
        sx={{
          pointerEvents: "none",
          position: "absolute",
          inset: 0,
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.3s ease",
          background: `radial-gradient(400px circle at ${mousePosition.x}px ${mousePosition.y}px, ${glowColor}, transparent 80%)`,
          zIndex: 1,
        }}
      />

      {/* Inner Content */}
      <Box sx={{ position: "relative", zIndex: 2, height: "100%" }}>{children}</Box>
    </Box>
  );
};

export default MagicCard;
